import { useEffect, useState } from 'react'
import { IconoCopiar, IconoCorrecto, IconoHoja } from '../../componentes/iconos'

/**
 * Como tiene que venir la hoja que se pega en el modo masivo.
 *
 * El formato estaba solo descrito en un parrafo y en el `placeholder` del
 * textarea, que desaparece en cuanto se escribe la primera letra. Quien prepara
 * la lista en Excel lo hace **antes** de abrir el dashboard, asi que el formato
 * tiene que poder consultarse y, sobre todo, **llevarse a Excel**: el boton
 * copia la plantilla con tabuladores, que es como Excel reparte las columnas al
 * pegar.
 *
 * La plantilla incluye la cabecera a proposito, aunque el analizador la ignora
 * (`CABECERAS` en `lista.ts`). En Excel una hoja sin cabecera se rellena mal a
 * la tercera fila, y aqui no estorba: el que la pegue de vuelta con cabecera
 * obtiene el mismo resultado que sin ella.
 */

interface Columna {
  nombre: string
  obligatoria: boolean
  explicacion: string
}

const COLUMNAS: Columna[] = [
  { nombre: 'NIF', obligatoria: true, explicacion: 'El del cliente, con o sin puntos y guiones.' },
  {
    nombre: 'Bono',
    obligatoria: false,
    explicacion: 'Cuántas garrafas lleva: 3, 4, 5... Déjalo en blanco si no hay bono.',
  },
  {
    nombre: 'Litraje',
    obligatoria: false,
    explicacion: 'Del bono: 18.9, 12.8, 20. Vale 18,9 y 18,9 L.',
  },
  {
    nombre: 'Productos',
    obligatoria: false,
    explicacion: 'Los que sean, separados con +. La cantidad va detrás: MANANTIAL 18.9L x 2.',
  },
]

const EJEMPLOS: string[][] = [
  ['20468583D', '4', '18.9', 'dispensador'],
  ['B12899720', '3', '12.8', ''],
  ['A12345674', '', '', 'MANANTIAL 18.9L x 2 + DISPENSADOR DE AGUA'],
  ['12345678Z', '', '', 'Pack Manantial 1.5L x 6'],
]

/** Con tabuladores: es lo que Excel reparte en columnas al pegar. */
export const PLANTILLA_EXCEL = [
  COLUMNAS.map((columna) => columna.nombre),
  ...EJEMPLOS,
]
  .map((fila) => fila.join('\t'))
  .join('\n')

const REGLAS = [
  'El bono es opcional: casi un cuarto de los pedidos no lleva ninguno. Sin él, basta con rellenar Productos.',
  'La cantidad de un producto va al final y con x: "MANANTIAL 18.9L x 2". Sin ella se entiende 1.',
  'Se admite cantidad negativa para la devolución de envases: "DEVOLUCION ENVASE 18.9L x -2".',
  'Los nombres de producto tienen que existir en el Catálogo del panel, tal y como se llaman allí.',
  'Una fila por cliente. El mismo NIF dos veces se marca: serían dos albaranes a la misma puerta.',
  'La fecha de reparto no va en la hoja: se elige una vez aquí, para toda la lista.',
  'La cabecera puede quedarse. Si la primera celda es NIF, CIF, DNI o Cliente, se ignora.',
  'Una fila que no se entienda no bloquea a las demás: sale marcada en la revisión y se corrige ahí.',
]

export function PlantillaExcel() {
  const [copiado, setCopiado] = useState(false)
  const [fallo, setFallo] = useState(false)

  useEffect(() => {
    if (!copiado) return
    const temporizador = window.setTimeout(() => setCopiado(false), 2500)
    return () => window.clearTimeout(temporizador)
  }, [copiado])

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(PLANTILLA_EXCEL)
      setFallo(false)
      setCopiado(true)
    } catch {
      // Sin portapapeles (navegador antiguo, permiso denegado) la tabla de
      // arriba sigue siendo la plantilla: se puede escribir a mano.
      setFallo(true)
    }
  }

  return (
    <details className="rounded-lg border border-borde bg-elevado/60">
      <summary className="flex cursor-pointer list-none items-center gap-2 px-3 py-2 text-sm font-medium text-tinta-2">
        <IconoHoja aria-hidden="true" className="size-4 text-tinta-apagada" />
        <span>Cómo debe ir el Excel</span>
      </summary>

      <div className="flex flex-col gap-3 border-t border-borde px-3 py-3">
        <div className="overflow-x-auto">
          <table className="w-full min-w-100 border-collapse text-left text-sm">
            <thead>
              <tr>
                {COLUMNAS.map((columna) => (
                  <th
                    key={columna.nombre}
                    scope="col"
                    className="border border-borde bg-superficie px-2 py-1.5 font-semibold text-tinta"
                  >
                    {columna.nombre}
                    {columna.obligatoria ? null : (
                      <span className="ml-1 font-normal text-tinta-apagada">(opcional)</span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {EJEMPLOS.map((fila) => (
                <tr key={fila[0]}>
                  {fila.map((celda, indice) => (
                    <td
                      key={COLUMNAS[indice].nombre}
                      className="border border-borde px-2 py-1.5 font-mono text-tinta-2"
                    >
                      {celda}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <dl className="grid gap-1 text-sm sm:grid-cols-[auto_1fr] sm:gap-x-3">
          {COLUMNAS.map((columna) => (
            <div key={columna.nombre} className="contents">
              <dt className="font-medium text-tinta-2">{columna.nombre}</dt>
              <dd className="text-tinta-apagada">{columna.explicacion}</dd>
            </div>
          ))}
        </dl>

        <ul className="flex list-disc flex-col gap-1 pl-5 text-sm text-tinta-apagada">
          {REGLAS.map((regla) => (
            <li key={regla}>{regla}</li>
          ))}
        </ul>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={copiar}
            className="inline-flex items-center gap-2 rounded-lg border border-borde-fuerte bg-superficie px-3 py-2 text-sm font-medium text-tinta transition-colors hover:bg-elevado"
          >
            {copiado ? (
              <IconoCorrecto aria-hidden="true" className="size-4 text-positivo-punto" />
            ) : (
              <IconoCopiar aria-hidden="true" className="size-4 text-tinta-apagada" />
            )}
            <span>{copiado ? 'Plantilla copiada' : 'Copiar plantilla'}</span>
          </button>
          <p className="text-sm text-tinta-apagada">
            {fallo
              ? 'No se ha podido usar el portapapeles. Copia la tabla de arriba a mano.'
              : 'Pégala en una hoja en blanco y se reparte en columnas.'}
          </p>
        </div>
      </div>
    </details>
  )
}
